import React from 'react';
import {
	FieldPath,
	FieldValues,
	useFormContext,
	useController,
} from 'react-hook-form';
import {
	FormControl,
	FormLabel,
	FormErrorMessage,
	Input,
	Text,
} from 'src/components/ui';

interface PhoneInputProps<T extends FieldValues = FieldValues> {
	name: FieldPath<T>;
	label?: string;
	placeholder?: string;
	required?: boolean;
	error?: string;
	onChange?: (value: string) => void;
	disableAutoFormat?: boolean;
}

const PhoneInput = <T extends FieldValues = FieldValues>({
	name,
	label = 'Phone Number',
	placeholder = 'e.g. 555-123-4567',
	required = false,
	error,
	onChange,
	disableAutoFormat = false,
}: PhoneInputProps<T>) => {
	const { control } = useFormContext();
	const { field } = useController({ name, control });
	const { onChange: fieldOnChange, value } = field;

	// Only format phone numbers when auto-formatting is enabled
	const formatPhoneNumber = (value: string): string => {
		if (disableAutoFormat) return value;

		// Remove all non-digit characters
		const digits = value.replace(/\D/g, '');

		// Handle US phone numbers (11 digits starting with 1)
		if (digits.length === 11 && digits.startsWith('1')) {
			return `+${digits.slice(0, 1)} ${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
		}

		// Handle 10-digit US numbers (add +1)
		if (digits.length === 10) {
			return `+1 ${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
		}

		// Return original value if it doesn't match patterns
		return value;
	};

	const checkAndFormatValue = (value: string): string | null => {
		if (disableAutoFormat) return null;

		// Check if this looks like an unformatted phone number
		const digits = value.replace(/\D/g, '');
		const isUnformattedNumber =
			digits.length >= 10 && digits.length <= 11 && !value.includes('+');

		if (isUnformattedNumber) {
			const formattedValue = formatPhoneNumber(value);
			if (formattedValue !== value) {
				return formattedValue;
			}
		}
		return null;
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disableAutoFormat) {
			// When auto-formatting is disabled, just pass through the value
			fieldOnChange(e);
			onChange?.(e.target.value);
			return;
		}

		// Original formatting logic for backward compatibility
		const value = e.target.value;
		const formattedValue = checkAndFormatValue(value);

		if (formattedValue) {
			// Create a synthetic event with the formatted value
			const syntheticEvent = {
				...e,
				target: {
					...e.target,
					value: formattedValue,
				},
			};
			fieldOnChange(syntheticEvent);
			onChange?.(formattedValue);
		} else {
			fieldOnChange(e);
			onChange?.(value);
		}
	};

	return (
		<FormControl isInvalid={!!error}>
			<FormLabel>
				{label}
				{required && (
					<Text as="span" color="red.500" ml={1}>
						*
					</Text>
				)}
			</FormLabel>
			<Input
				value={value || ''}
				autoComplete="tel"
				placeholder={placeholder}
				onChange={handleChange}
				bg="white"
				fontSize="14px"
				fontWeight="400"
				lineHeight="normal"
				_placeholder={{ color: '#A0AEC0' }}
				borderRadius="5px"
			/>
			{error && <FormErrorMessage>{error}</FormErrorMessage>}
		</FormControl>
	);
};

export default PhoneInput;
